import { useState, useEffect, useMemo } from "react";
import { X, Save, Package2 } from "lucide-react";
import ImageUploader from "../ImageUploader";
import { useTranslation } from "react-i18next";

interface VariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (variations: any[], isEditing: boolean) => Promise<void>;
  productId: string;
  productPrice: number;
  variation?: any;
}

function parseCsv(val: string): string[] {
  return val
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildSku(productId: string, parts: string[]): string {
  return `${productId}-${parts
    .filter(Boolean)
    .map((p) =>
      p
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
    )
    .join("-")}`;
}

// Predefined color options with hex values
export const COLOR_OPTIONS = [
  { name: "Red", hex: "#EF4444" },
  { name: "Blue", hex: "#3B82F6" },
  { name: "Green", hex: "#10B981" },
  { name: "Yellow", hex: "#F59E0B" },
  { name: "Purple", hex: "#A855F7" },
  { name: "Pink", hex: "#EC4899" },
  { name: "Orange", hex: "#F97316" },
  { name: "Black", hex: "#000000" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Gray", hex: "#6B7280" },
  { name: "Navy", hex: "#1E3A8A" },
  { name: "Teal", hex: "#14B8A6" },
  { name: "Brown", hex: "#92400E" },
  { name: "Beige", hex: "#D4C5B9" },
  { name: "Maroon", hex: "#7F1D1D" },
  { name: "Olive", hex: "#84CC16" },
];

const VariationModal: React.FC<VariationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  productId,
  productPrice,
  variation = null,
}) => {
  const isEditing = !!variation;
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();

  // Attribute fields
  const [color, setColor] = useState("");
  const [sizeInput, setSizeInput] = useState(""); // single value in edit, comma-separated in add
  const [styleInput, setStyleInput] = useState(""); // single value in edit, comma-separated in add
  const [sku, setSku] = useState(""); // only used in edit mode

  // Shared fields
  const [description, setDescription] = useState({ en: "", ar: "" });
  const [weight, setWeight] = useState<number | null>(null);
  const [price, setPrice] = useState<number | null>(productPrice);
  const [priceAdjustment, setPriceAdjustment] = useState(0);
  const [stock, setStock] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (variation) {
      setColor(
        variation.attributes?.color || variation.attributes?.colour || "",
      );
      setSizeInput(variation.attributes?.size || "");
      setStyleInput(variation.attributes?.style || "");
      setSku(variation.sku || "");
      setDescription({
        en: variation.description?.en || "",
        ar: variation.description?.ar || "",
      });
      setWeight(variation.weight ?? null);
      setPrice(variation.price);
      setPriceAdjustment(variation.priceAdjustment || 0);
      setStock(variation.stock || 0);
      setImages(variation.images || []);
      setIsActive(variation.isActive ?? true);
    } else {
      resetForm();
    }
    setErrors({});
  }, [variation, isOpen]);

  const resetForm = () => {
    setColor("");
    setSizeInput("");
    setStyleInput("");
    setSku("");
    setDescription({ en: "", ar: "" });
    setWeight(null);
    setPrice(productPrice);
    setPriceAdjustment(0);
    setStock(0);
    setImages([]);
    setIsActive(true);
  };

  // Cartesian product of all attribute combinations (add mode only)
  const combinations = useMemo(() => {
    if (isEditing) return [];
    const sizes = parseCsv(sizeInput);
    const styles = parseCsv(styleInput);
    if (sizes.length === 0) return [];
    const result: { color: string; size: string; style: string }[] = [];
    for (const size of sizes) {
      if (styles.length > 0) {
        for (const style of styles) {
          result.push({ color, size, style });
        }
      } else {
        result.push({ color, size, style: "" });
      }
    }
    return result;
  }, [isEditing, color, sizeInput, styleInput]);

  const getSelectedColor = () => COLOR_OPTIONS.find((c) => c.name === color);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!sizeInput.trim()) {
      e.size = t("Size is required");
    } else if (!isEditing && combinations.length === 0) {
      e.size = t("Enter at least one valid size");
    }
    if (isEditing && !sku.trim()) e.sku = t("SKU is required");
    if (price !== null && price < 0) e.price = t("Price cannot be negative");
    if (stock < 0) e.stock = t("Stock cannot be negative");
    if (weight !== null && weight < 0)
      e.weight = t("Weight cannot be negative");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEditing) {
        const attributes: Record<string, string> = {};
        if (color) attributes.color = color;
        if (sizeInput.trim()) attributes.size = sizeInput.trim();
        if (styleInput.trim()) attributes.style = styleInput.trim();
        await onSave(
          [
            {
              productId,
              id: variation?.id,
              attributes,
              description,
              weight,
              price,
              priceAdjustment,
              stock,
              sku,
              images,
              isActive,
            },
          ],
          true,
        );
      } else {
        const variationsToCreate = combinations.map((combo) => {
          const attributes: Record<string, string> = {};
          if (combo.color) attributes.color = combo.color;
          attributes.size = combo.size;
          if (combo.style) attributes.style = combo.style;
          return {
            productId,
            attributes,
            description,
            weight,
            price,
            priceAdjustment,
            stock,
            sku: buildSku(productId, [combo.color, combo.size, combo.style]),
            images,
            isActive,
          };
        });
        await onSave(variationsToCreate, false);
      }
      handleClose();
    } catch (error) {
      console.error("Error saving variation:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    resetForm();
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => e.stopPropagation()}
      style={{ animation: "fadeIn 0.3s" }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-2xl shadow-2xl sm:max-w-3xl sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "slideUp 0.3s" }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-black to-gray-700 px-4 sm:px-6 py-4 sm:rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <h3 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
            <Package2 className="w-5 h-5 sm:w-6 sm:h-6" />
            {isEditing ? t("Edit Variation") : t("Add Variation")}
          </h3>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-5">
          {/* Attributes Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-1">
              {t("Variation Attributes")}
            </h4>
            {!isEditing && (
              <p className="text-xs text-gray-500 mb-4">
                {t(
                  "All size × style combinations will be auto-generated. Color is a single selection (optional); Size is required and can be comma-separated; Style is optional and can be comma-separated.",
                )}
              </p>
            )}

            <div className="grid sm:grid-cols-3 gap-4">
              {/* Color Selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Color")}{" "}
                </label>
                <div className="relative">
                  <select
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    className="w-full px-4 py-3 pr-10 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all appearance-none"
                  >
                    <option value="">{t("Select a color")}</option>
                    {COLOR_OPTIONS.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {color && getSelectedColor() && (
                    <div
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full border-2 border-gray-300"
                      style={{ backgroundColor: getSelectedColor()?.hex }}
                    />
                  )}
                </div>
                {color && (
                  <div className="mt-2 flex items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full border-2 border-gray-300"
                      style={{ backgroundColor: getSelectedColor()?.hex }}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      {color}
                    </span>
                  </div>
                )}
              </div>

              {/* Size Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Size")} <span className="text-red-500">*</span>
                  {!isEditing && (
                    <span className="font-normal text-gray-400 ml-1">
                      ({t("comma-separated")})
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={sizeInput}
                  onChange={(e) => {
                    setSizeInput(
                      isEditing ? e.target.value.toUpperCase() : e.target.value,
                    );
                    if (errors.size)
                      setErrors((prev) => ({ ...prev, size: "" }));
                  }}
                  placeholder={isEditing ? "e.g., M" : "e.g., 51, 52, 53"}
                  className={`w-full px-4 py-3 rounded-lg border-2 ${
                    errors.size
                      ? "border-red-500"
                      : "border-gray-200 focus:border-black"
                  } focus:outline-none transition-all`}
                />
                {errors.size ? (
                  <p className="mt-1 text-xs text-red-500">{errors.size}</p>
                ) : (
                  !isEditing && (
                    <p className="mt-1 text-xs text-gray-500">
                      {t("Enter multiple sizes separated by commas")}
                    </p>
                  )
                )}
              </div>

              {/* Style Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t("Style")}{" "}
                  {!isEditing && (
                    <span className="font-normal text-gray-400">
                      ({t("comma-separated")})
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={styleInput}
                  onChange={(e) => setStyleInput(e.target.value)}
                  placeholder={
                    isEditing ? "e.g., Regular" : "e.g., Regular, Standard"
                  }
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                />
                {!isEditing && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t("Enter multiple styles separated by commas")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Combinations Preview (add mode only) */}
          {!isEditing && combinations.length > 0 && (
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <h4 className="font-semibold text-green-700 mb-3">
                {combinations.length} {t("variation(s) will be created")}
              </h4>
              <div className="flex flex-wrap gap-2">
                {combinations.map((combo, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 bg-white border border-green-200 text-sm text-gray-700 px-3 py-1.5 rounded-full"
                  >
                    {combo.color && (
                      <>
                        <span
                          className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0"
                          style={{
                            backgroundColor: COLOR_OPTIONS.find(
                              (c) => c.name === combo.color,
                            )?.hex,
                          }}
                        />
                        <span>{combo.color}</span>
                        <span className="text-gray-400">·</span>
                      </>
                    )}
                    <span>{combo.size}</span>
                    {combo.style && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span>{combo.style}</span>
                      </>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* SKU (edit mode only, read-only) */}
          {isEditing && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("SKU")}
              </label>
              <input
                type="text"
                value={sku}
                disabled
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-gray-100 cursor-not-allowed focus:outline-none"
              />
            </div>
          )}

          {/* Price Options */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Fixed Price")}
              </label>
              <input
                type="number"
                step="0.01"
                value={price || 0}
                onChange={(e) => {
                  setPrice(e.target.value ? parseFloat(e.target.value) : null);
                  if (errors.price)
                    setErrors((prev) => ({ ...prev, price: "" }));
                }}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.price
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder="Leave empty to use base price"
              />
              {errors.price && (
                <p className="mt-1 text-xs text-red-500">{errors.price}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {t("Override the base product price")}
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Price Adjustment")}
              </label>
              <input
                type="number"
                step="0.01"
                value={priceAdjustment}
                onChange={(e) =>
                  setPriceAdjustment(parseFloat(e.target.value) || 0)
                }
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
                placeholder="0.00"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("Add/subtract from base price")}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Stock */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Stock Quantity")} *
              </label>
              <input
                type="number"
                value={stock}
                onChange={(e) => {
                  setStock(parseInt(e.target.value) || 0);
                  if (errors.stock)
                    setErrors((prev) => ({ ...prev, stock: "" }));
                }}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.stock
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder="0"
              />
              {errors.stock && (
                <p className="mt-2 text-sm text-red-500">{errors.stock}</p>
              )}
            </div>

            {/* Weight */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {t("Weight (grams)")}
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={weight ?? ""}
                onChange={(e) => {
                  setWeight(
                    e.target.value === "" ? null : parseInt(e.target.value),
                  );
                  if (errors.weight)
                    setErrors((prev) => ({ ...prev, weight: "" }));
                }}
                className={`w-full px-4 py-3 rounded-lg border-2 ${
                  errors.weight
                    ? "border-red-500"
                    : "border-gray-200 focus:border-black"
                } focus:outline-none transition-all`}
                placeholder="100"
              />
              {errors.weight && (
                <p className="mt-2 text-sm text-red-500">{errors.weight}</p>
              )}
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("Variation Images (Optional)")}
            </label>
            <ImageUploader
              value={images}
              onChange={(value: string | string[]) =>
                setImages(Array.isArray(value) ? value : [value])
              }
              multiple={true}
              label=""
            />
            <p className="text-xs text-gray-500 mt-2">
              {t("Add specific images for this variation (optional)")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("English Description")}
            </label>
            <textarea
              value={description.en}
              onChange={(e) =>
                setDescription((prev) => ({ ...prev, en: e.target.value }))
              }
              rows={3}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
              placeholder={t("Product description")}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("Arabic Description")}
            </label>
            <textarea
              value={description.ar}
              onChange={(e) =>
                setDescription((prev) => ({ ...prev, ar: e.target.value }))
              }
              rows={3}
              className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:border-black focus:outline-none transition-all"
              placeholder="وصف المنتج"
              dir="rtl"
            />
          </div>

          {/* Active Status */}
          <div className="pt-4 border-t-2 border-gray-100">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-5 h-5 rounded"
              />
              <div>
                <span className="text-sm font-semibold text-gray-700">
                  {t("Active Variation")}
                </span>
                <p className="text-xs text-gray-500">
                  {t("Variation will be available for purchase")}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 flex flex-col sm:flex-row gap-2 sm:gap-3 sticky bottom-0 bg-white border-t sm:border-0 pt-4">
          <button
            onClick={handleClose}
            className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-300 font-semibold transition-all"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-700 font-semibold disabled:opacity-70 flex items-center justify-center gap-2 transition-all"
          >
            {saving ? (
              <>
                <div
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                  style={{ animation: "spin 1s linear infinite" }}
                />
                <span>{t("Saving...")}</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEditing
                  ? t("Update Variation")
                  : combinations.length > 1
                    ? `${t("Create")} ${combinations.length} ${t("Variations")}`
                    : t("Create Variation")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VariationModal;
